import type { EstadoNodo } from "../grafo/definicion";

/**
 * El estado de UNA fuente de datos, tal como lo muestra la pantalla de datos.
 *
 * Está separado de `fuentesConectadas.ts` a propósito por dos motivos:
 *
 *  1. Es lógica PURA y se testea sin DOM. `fuentesConectadas.ts` importa
 *     `lib/supabase` (que crea el cliente al cargar el módulo), así que un test
 *     que lo importara arrastraría medio backend.
 *  2. "Falló la consulta" y "sin conectar" son estados DISTINTOS y el único
 *     lugar donde se decide cuál es cuál tiene que ser uno solo. Mostrar un
 *     error de red como "sin conectar" le miente al usuario sobre su propia
 *     cuenta: lo manda a reconectar algo que ya estaba conectado.
 */

export type EstadoConexionFuente =
  /** La consulta está en vuelo. */
  | "cargando"
  /** La fuente está conectada de verdad. */
  | "conectado"
  /** La consulta respondió y dijo que no está conectada. */
  | "sin-conectar"
  /** La consulta NO respondió: no se sabe si está conectada o no. */
  | "error"
  /** No hay integración: no se puede conectar todavía (Meta Ads). */
  | "no-disponible";

export interface LecturaFuente {
  cargando: boolean;
  conectado: boolean;
  /** La consulta de esta fuente falló (red, permisos, función caída). */
  fallo: boolean;
}

/**
 * `cargando` gana sobre `fallo`: si hay una consulta nueva en vuelo (un
 * "Reintentar", o un cambio de marca), el error que quedó de la anterior es
 * información vieja y mostrarlo sería tan falso como mostrar el "conectado"
 * de la marca anterior.
 *
 * `conectado` sólo se cree cuando la consulta terminó y NO falló — nunca se
 * deriva "conectado" de un booleano que quedó en `false` por un error.
 */
export function estadoDeFuente(lectura: LecturaFuente): EstadoConexionFuente {
  if (lectura.cargando) return "cargando";
  if (lectura.fallo) return "error";
  return lectura.conectado ? "conectado" : "sin-conectar";
}

/**
 * El mismo estado, traducido al vocabulario del MAPA (`EstadoNodo`). Existe
 * para que el nodo del mapa y la tarjeta de la pantalla de datos —que están a
 * un clic de distancia y contestan la misma pregunta— no puedan contestarla
 * distinto: el mapa derivaba su color con un ternario propio sobre el booleano
 * (`fuentes.instagram ? "activo" : "sin-conectar"`), así que una consulta caída
 * se pintaba de "sin conectar" mientras la tarjeta decía "No pudimos
 * consultarlo".
 *
 * `cargando` va a `desconocido` por el mismo motivo que `error`: mientras la
 * consulta está en vuelo no se sabe, y el punteado gris de `sin-conectar` es
 * una afirmación. `no-disponible` (Meta Ads) SÍ es `sin-conectar`: ahí no falta
 * la respuesta, falta la integración, y el mapa ya la dibuja apagada.
 */
export function estadoNodoDeFuente(estado: EstadoConexionFuente): EstadoNodo {
  if (estado === "conectado") return "activo";
  if (estado === "cargando" || estado === "error") return "desconocido";
  return "sin-conectar";
}
